import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export default function ClientsPage() {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', company_name: '', contact_name: '', phone: '', service: '', website: '' });
  const [creating, setCreating] = useState(false);

  const loadClients = () => api('get', `/api/admin/clients?search=${search}`).then(r => setClients(r.data.clients || [])).finally(() => setLoading(false));
  useEffect(() => { loadClients(); }, [search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api('post', '/api/admin/clients/create', form);
      // Toon duidelijk feedback over email-status — voorheen kreeg admin
      // silent success ook als SMTP faalde.
      const status = r.data.email_status;
      if (status === 'sent') {
        toast.success(`Klant aangemaakt en uitnodiging verstuurd naar ${form.email}`);
      } else if (status === 'queued_after_failure') {
        toast.warning(`Klant aangemaakt — SMTP traag, email staat in retry queue (${r.data.email_error || 'onbekende fout'}). We proberen automatisch opnieuw.`);
      } else if (status === 'error') {
        toast.error(`Klant aangemaakt maar email mislukte: ${r.data.email_error || 'onbekende fout'}. Gebruik "Verstuur uitnodiging opnieuw" op de klantkaart.`);
      } else {
        toast.info(`Klant aangemaakt. Email status: ${status || 'onbekend'}`);
      }
      setShowCreate(false);
      setForm({ email: '', company_name: '', contact_name: '', phone: '', service: '', website: '' });
      loadClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Aanmaken mislukt'); }
    finally { setCreating(false); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Klanten</h1>
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-4 py-2 rounded-xl text-sm transition-all">
          <Plus size={16} /> Klant aanmaken
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-[#0f172a] rounded-2xl border border-[#D4A017]/30 p-6 mb-6">
          <h3 className="text-white font-bold mb-4">Nieuwe klant aanmaken</h3>
          <form onSubmit={handleCreate} className="grid sm:grid-cols-2 gap-4">
            {[
              ['Bedrijfsnaam *', 'company_name', 'text', 'Bedrijfsnaam BV'],
              ['Email *', 'email', 'email', 'klant@bedrijf.nl'],
              ['Contactpersoon', 'contact_name', 'text', 'Naam'],
              ['Telefoon', 'phone', 'tel', '06-'],
              ['Website', 'website', 'url', 'https://'],
              ['Dienst', 'service', 'text', 'bijv. Google Ads + SEO'],
            ].map(([label, field, type, ph]) => (
              <div key={field}>
                <label className="block text-sm font-medium text-[#94a3b8] mb-1">{label}</label>
                <input type={type} value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} placeholder={ph}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017] text-sm" />
              </div>
            ))}
            <div className="sm:col-span-2 flex gap-3">
              <button type="submit" disabled={creating} className="bg-[#D4A017] text-[#0f172a] font-bold px-6 py-2 rounded-lg text-sm disabled:opacity-60">
                {creating ? 'Aanmaken...' : 'Aanmaken & welkomstmail sturen'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="bg-white/10 text-white px-6 py-2 rounded-lg text-sm">Annuleren</button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek op bedrijfsnaam..."
          className="w-full bg-[#0f172a] border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder-[#475569] focus:outline-none focus:border-[#D4A017]" />
      </div>

      {/* Table */}
      <div className="bg-[#0f172a] rounded-2xl border border-white/5 overflow-hidden">
        <div className="grid grid-cols-4 text-xs font-bold text-[#475569] uppercase px-5 py-3 border-b border-white/5">
          <span>Bedrijf</span><span>Status</span><span>Contract</span><span>Aangemaakt</span>
        </div>
        {loading ? (
          <div className="py-12 text-center"><div className="w-6 h-6 border-2 border-[#D4A017] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : clients.length === 0 ? (
          <div className="py-12 text-center text-[#475569]">Geen klanten gevonden</div>
        ) : clients.map(c => (
          <Link key={c.id} to={`/admin/clients/${c.id}`} className="grid grid-cols-4 items-center px-5 py-4 hover:bg-white/5 border-b border-white/5 last:border-0 transition-all group">
            <div>
              <p className="text-white text-sm font-medium group-hover:text-[#D4A017] transition-colors">{c.name}</p>
              <p className="text-[#475569] text-xs">{c.service || 'Geen dienst'}</p>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full w-fit ${c.onboarding_status === 'complete' ? 'bg-green-500/20 text-green-400' : c.onboarding_status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
              {c.onboarding_status === 'complete' ? 'Voltooid' : c.onboarding_status === 'in_progress' ? 'Bezig' : 'Nieuw'}
            </span>
            <span className="text-[#64748b] text-sm">{c.contract ? `€${c.contract.price}/mnd` : 'Geen'}</span>
            <span className="text-[#475569] text-sm">{c.created_at?.slice(0, 10)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
